import { faGear } from '@fortawesome/pro-regular-svg-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from '@tanstack/react-router';
import {
	ConditionType,
	type UturnUserResponse,
	useFetchOrganizationById,
	useFetchUturnUserById,
	WeightUnit,
} from '@uturn/api/v1';
import { MetaDataContext } from '@uturn/portal/context';
import { useAddShipmentStore } from '@uturn/portal/store/shipments/add-shipment';
import {
	ContainerStatus,
	TransportTypeCode,
	TypeOfShipment,
} from '@uturn/portal/types/shipment';
import { getApiErrorMessage } from '@uturn/portal/utils';
import { z } from '@uturn/portal/zod';
import {
	Button,
	Card,
	CardContent,
	CardFooter,
	CardHeader,
	CardTitle,
	Form,
	FormControl,
	FormField,
	FormItem,
	FormMessage,
	FormOptionsType,
	FormRadioGroupHorizontalItem,
	Icon,
	RadioGroup,
	sonner,
} from '@uturn/ui-kit';
import { useContext, useEffect, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { EditDefaultValuesModal } from '../../components';
import {
	shouldUpdate,
	transportTypeName,
	typeOfShipmentDescription,
	typeOfShipmentIcon,
	typeOfShipmentTitle,
} from '../../utils';

export function AddShipmentForm() {
	const { t } = useTranslation();
	const { shipmentMetaData, metadata } = useContext(MetaDataContext);
	const router = useRouter();

	const {
		initData,
		orgDefaultValues,
		currencyCode,
		setManualCreateInit,
		setDefaultValues,
		setOrgDefaultValues,
		setCurrencyCode,
	} = useAddShipmentStore();

	const orgId = metadata?.user?.organisation?.id;

	const {
		data: org,
		isPending: isOrgPending,
		isSuccess: isOrgSuccess,
		isError: isOrgError,
		error: orgError,
	} = useFetchOrganizationById(orgId ?? -1, {
		query: {
			enabled: !!orgId,
			select: (data) => data.data,
		},
	});

	// TODO: Improve this deprecated onSuccess/onError workaround.
	useEffect(() => {
		if (isOrgError) {
			sonner.error(
				t(
					'pages.addshipment.flow.org.api-error.title',
					'Could not load organization settings',
				),
				{
					description: getApiErrorMessage(orgError),
				},
			);
		}
		if (isOrgSuccess) {
			const { invoiceRequired, shipmentCondition } = org.settings ?? {};

			setOrgDefaultValues({
				invoiceRequired,
				conditions: shipmentCondition
					? ConditionType[shipmentCondition]
					: undefined,
			});

			const { currency } = org.financialDetails ?? {};

			if (currency) {
				setCurrencyCode(currency);
			}
		}
	}, [
		isOrgError,
		orgError,
		isOrgSuccess,
		org,
		setOrgDefaultValues,
		setCurrencyCode,
	]);

	const userId = metadata?.user?.id;

	const {
		data: user,
		isPending: isUserPending,
		isSuccess: isUserSuccess,
		isError: isUserError,
		error: userError,
	} = useFetchUturnUserById(userId ?? -1, {
		query: {
			enabled: !!userId,
			select: (data) => data.data,
		},
	});

	// Copy the user's prefilled settings into the create-shipment store. Called
	// on initial load and again whenever the defaults are saved from the modal,
	// so a reset/update takes effect without a full page reload.
	const syncUserDefaults = (settings: UturnUserResponse['settings']) => {
		const { fixedPrice, shippingLineId, requiresVat, containerStatus } =
			settings ?? {};

		const defaultShippingLine =
			shippingLineId && shippingLineId > -1
				? shipmentMetaData?.shippingLines?.find(
						(item) => item.id === shippingLineId,
					)
				: undefined;

		setDefaultValues({
			fixedPrice,
			dutiesPaid: requiresVat,
			containerStatus: containerStatus
				? ContainerStatus[containerStatus]
				: undefined,
			grossWeight: {
				quantity: 0,
				unit: WeightUnit.kilogram,
			},
			shippingLineId: defaultShippingLine?.id,
			shippingLine: defaultShippingLine,
		});
	};

	// TODO: Improve this deprecated onSuccess/onError workaround.
	useEffect(() => {
		if (isUserError) {
			sonner.error(
				t(
					'pages.addshipment.flow.user.api-error.title',
					'Could not load user settings',
				),
				{
					description: getApiErrorMessage(userError),
				},
			);
		}
		if (isUserSuccess) {
			syncUserDefaults(user.settings);
		}
	}, [isUserError, userError, isUserSuccess]);

	const [openEditDefaultValuesModal, setOpenEditDefaultValuesModal] =
		useState(false);

	const [transportTypeResetKey, setTransportTypeResetKey] = useState(0);

	const formSchema = z.object({
		transportType: z.nativeEnum(TransportTypeCode),
		typeOfShipment: z.nativeEnum(TypeOfShipment),
	});

	type AddShipmentFormValues = Partial<z.infer<typeof formSchema>>;

	const form = useForm<AddShipmentFormValues>({
		resolver: zodResolver(formSchema),
		values: {
			transportType: user?.settings.transportType
				? TransportTypeCode[user.settings.transportType]
				: undefined,
		},
		resetOptions: { keepDirtyValues: true },
	});

	const onSubmit: SubmitHandler<AddShipmentFormValues> = (formData) => {
		const { transportType, typeOfShipment } = formData;

		if (transportType === undefined || typeOfShipment === undefined) {
			return;
		}

		setManualCreateInit(
			transportType,
			typeOfShipment,
			initData,
			orgDefaultValues,
			currencyCode,
		);

		if (typeOfShipment === TypeOfShipment.MULTI) {
			router.navigate({
				to: '/shipments/add/bulk',
				search: { transportType },
			});
			return;
		}

		router.navigate({ to: '/shipments/add/manual' });
	};

	const transportTypeOptions: FormOptionsType<TransportTypeCode>[] = [
		{
			id: TransportTypeCode.IMPORT,
			value: TransportTypeCode.IMPORT,
			label: transportTypeName(t)[TransportTypeCode.IMPORT],
		},
		{
			id: TransportTypeCode.EXPORT,
			value: TransportTypeCode.EXPORT,
			label: transportTypeName(t)[TransportTypeCode.EXPORT],
		},
		{
			id: TransportTypeCode.SHUNT,
			value: TransportTypeCode.SHUNT,
			label: transportTypeName(t)[TransportTypeCode.SHUNT],
		},
	];

	const typeOfShipmentOptions: FormOptionsType<TypeOfShipment>[] = [
		{
			id: TypeOfShipment.SINGLE,
			value: TypeOfShipment.SINGLE,
			icon: typeOfShipmentIcon[TypeOfShipment.SINGLE],
			label: typeOfShipmentTitle(t)[TypeOfShipment.SINGLE],
			description: typeOfShipmentDescription(t)[TypeOfShipment.SINGLE],
		},
		{
			id: TypeOfShipment.GROUP,
			value: TypeOfShipment.GROUP,
			icon: typeOfShipmentIcon[TypeOfShipment.GROUP],
			label: typeOfShipmentTitle(t)[TypeOfShipment.GROUP],
			description: typeOfShipmentDescription(t)[TypeOfShipment.GROUP],
		},
		{
			id: TypeOfShipment.MULTI,
			value: TypeOfShipment.MULTI,
			icon: typeOfShipmentIcon[TypeOfShipment.MULTI],
			label: typeOfShipmentTitle(t)[TypeOfShipment.MULTI],
			description: typeOfShipmentDescription(t)[TypeOfShipment.MULTI],
		},
	];

	return (
		<>
			<Form {...form}>
				<form onSubmit={form.handleSubmit(onSubmit)}>
					<Card>
						<CardHeader>
							<CardTitle className="flex items-center justify-between gap-2">
								<span className="flex-1">
									<Button
										variant="outline"
										type="button"
										className="flex gap-2"
										onClick={() => setOpenEditDefaultValuesModal(true)}
									>
										<Icon icon={faGear} />
										{t(
											'pages.addshipment.flow.action.defaults',
											'Change defaults',
										)}
									</Button>
								</span>
								<span className="flex-none">
									{t('pages.addshipment.flow.title', 'Create shipment')}
								</span>
								<span className="flex-1"></span>
							</CardTitle>
						</CardHeader>
						<CardContent className="space-y-6">
							<div className="space-y-4">
								<p className="text-muted-foreground text-center text-sm">
									{t(
										'pages.addshipment.flow.transport-type.description',
										'Select a transport type to create a new shipment.',
									)}
								</p>
								<FormField
									control={form.control}
									name="transportType"
									render={({ field }) => (
										<FormItem>
											<FormControl>
												<RadioGroup
													key={transportTypeResetKey}
													value={field.value ?? ''}
													onValueChange={field.onChange}
													className="mt-0 grid grid-cols-3 gap-2 pt-0"
												>
													{transportTypeOptions.map((item) => {
														return (
															<FormRadioGroupHorizontalItem
																key={item.id}
																{...item}
															/>
														);
													})}
												</RadioGroup>
											</FormControl>
											<FormMessage className="text-center" />
										</FormItem>
									)}
								/>
							</div>
							<div className="space-y-4">
								<p className="text-muted-foreground text-center text-sm">
									{t(
										'pages.addshipment.flow.type-of-shipment.description',
										'How do you want to create your shipment(s)?',
									)}
								</p>
								<FormField
									control={form.control}
									name="typeOfShipment"
									render={({ field }) => (
										<FormItem>
											<FormControl>
												<RadioGroup
													value={field.value}
													onValueChange={field.onChange}
													className="grid gap-2 mt-0 pt-0 grid-cols-3"
												>
													{typeOfShipmentOptions.map((item) => {
														return (
															<FormRadioGroupHorizontalItem
																key={item.id}
																variant="card"
																{...item}
															/>
														);
													})}
												</RadioGroup>
											</FormControl>
											<FormMessage className="text-center" />
										</FormItem>
									)}
								/>
							</div>
						</CardContent>
						<CardFooter className="flex justify-center">
							<Button
								type="submit"
								variant="default"
								className="w-48"
								disabled={isOrgPending || isUserPending}
							>
								{t('pages.addshipment.flow.submit', 'Continue')}
							</Button>
						</CardFooter>
					</Card>
				</form>
			</Form>
			<EditDefaultValuesModal
				openModal={openEditDefaultValuesModal}
				setOpenModal={setOpenEditDefaultValuesModal}
				onSuccess={(data) => {
					syncUserDefaults(data.settings);
					const transportTypeString = data.settings.transportType;
					form.setValue(
						'transportType',
						transportTypeString
							? TransportTypeCode[transportTypeString]
							: undefined,
						shouldUpdate,
					);
					setTransportTypeResetKey((prev) => prev + 1);
				}}
			/>
		</>
	);
}
